/* eslint-disable sonarjs/cognitive-complexity */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { endOfDay } from 'date-fns'
import { ParsedUrlQuery } from 'querystring'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ServerExperiments } from '@/experimentation'
import { useToast } from '@/molecules/Toast'
import { getWebClient } from '@/services/ApolloClient'
import { useGuildMember, useUserGuildTickets } from '@/services/GuildUserService'
import { useNativeMobileShare } from '@/services/Sharing'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { TheatricalShowtime } from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'
import { LocaleWarningModalState } from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueList'
import { SharingModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/SharingModal'
import { parseTheatricalDate, publishTicketIntent } from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'
import { getLanguageName } from '@/views/TicketCheckoutViews/utils'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'

interface TicketsContextValues {
  theatricalRelease: NonNullable<TheatricalReleaseResult>
  countryCode: string | undefined
  query: ParsedUrlQuery
  routerIsReady: boolean
  openSharingModal: () => void
  closeSharingModal: () => void
  disableShowtimes: boolean
  setDisableShowtimes: React.Dispatch<React.SetStateAction<boolean>>
  guildTicketsCount: number
  guildDiscountCodes: string[]
  isFreeTicketPromo: boolean
  isKidsGoFree: boolean
  trackTicketIntent: (analyticsPayload: TicketsAnalyticsPayload) => void
  detectLanguageDifference: (showTime: TheatricalShowtime) => boolean
  handleMobileShare: () => void
  shouldHideDiscountsForMarketing: boolean
  isEditReservationFlow?: boolean
  handleLanguageWarning: (
    setLocaleWarningModalState: React.Dispatch<React.SetStateAction<LocaleWarningModalState>>,
    showTime: TheatricalShowtime,
    callback: () => void,
  ) => void
  earlyDateToTry?: Date
  experiments?: ServerExperiments
  basicPageExperimentEnabled?: boolean
  projectGuildScore?: number | null
}

export type DiscountType = { description: string; value: number; name?: string; code: string; category?: string }

const TicketsContext = React.createContext<TicketsContextValues | null>(null)

export type TicketsAnalyticsPayload = {
  venue: ShowtimesVenue
  latitude: Maybe<number>
  longitude: Maybe<number>
  theatricalName: Maybe<string>
  theatricalSlug: Maybe<string>
  projectSlug: Maybe<string>
}

interface TicketsContextProviderProps {
  theatricalRelease: NonNullable<TheatricalReleaseResult>
  isEditReservationFlow?: boolean
  defaultSearchDate?: Date
  experiments?: ServerExperiments
  basicPageExperimentEnabled?: boolean
  projectGuildScore?: number | null
}

const EARLY_DATE_TO_TRY_CONFIG: Record<string, Date> = {
  [slugs.david]: endOfDay(new Date(2025, 11, 14)),
}

export const TicketsContextProvider: ReactFCC<TicketsContextProviderProps> = ({
  children,
  theatricalRelease,
  isEditReservationFlow,
  experiments,
  basicPageExperimentEnabled,
  projectGuildScore,
}) => {
  const { asPath, query, isReady: routerIsReady } = useSafeRouter()
  const [displaySharingModal, setDisplaySharingModal] = useState(false)
  const [disableShowtimes, setDisableShowtimes] = useState(false)
  const { t } = useTranslate('tickets')
  const { showToast } = useToast()
  const { locale } = useLocale()
  const { track } = useSafeAnalytics()
  const { share } = useNativeMobileShare()
  const isDatesDisabled = false
  const currentPathWithoutQueryParams = asPath.split('?')[0] || ''
  const releaseDate = useMemo(() => {
    if (theatricalRelease.region?.releaseDate) {
      return parseTheatricalDate(new Date(theatricalRelease.region?.releaseDate).toISOString())
    }
    return null
  }, [theatricalRelease.region?.releaseDate])
  const isFreeTicketPromo = query?.promo === 'sof-claim-free' || query?.promo === 'claim-free-ticket'
  const isKidsGoFree = [slugs.david].includes(theatricalRelease.theatricalSlug)

  const earlyDateToTry = useMemo(() => {
    return EARLY_DATE_TO_TRY_CONFIG[theatricalRelease.theatricalSlug]
  }, [theatricalRelease.theatricalSlug])

  const closeSharingModal = useCallback(() => setDisplaySharingModal(false), [setDisplaySharingModal])

  const openSharingModal = useCallback(() => setDisplaySharingModal(true), [setDisplaySharingModal])
  const handleMobileShare = useCallback(async () => {
    await share({
      title: t('ticketsSEOTitleAngel', `{{theatricalName}} Tickets & Showtimes | Angel`, {
        theatricalName: theatricalRelease.title,
      }),
      text: t('ticketsSEODescription', 'Buy {{theatricalName}} tickets, watch trailers and find showtimes near you.', {
        theatricalName: theatricalRelease.title,
      }),
      url: `${paths.getBaseURL()}${currentPathWithoutQueryParams}`,
      onShareFail: openSharingModal,
    })
  }, [currentPathWithoutQueryParams, openSharingModal, share, t, theatricalRelease.title])

  const shouldHideDiscountsForMarketing = query?.promoOverride === 'true'

  const { isGuildMember } = useGuildMember()
  const client = getWebClient()
  const { numberAvailable: guildTicketsCount = 0, discountCodes } = useUserGuildTickets({
    theatricalSlug: theatricalRelease.theatricalSlug,
    client,
    skip: !isGuildMember,
  })

  const guildDiscountCodes = useMemo(() => {
    return discountCodes?.map((theatricalDiscount) => theatricalDiscount?.code)
  }, [discountCodes])

  const detectLanguageDifference = useCallback(
    (showTime: TheatricalShowtime) => {
      const showTimeLanguage = showTime?.language
      const subtitleLanguage = showTime?.subtitleLanguage
      const wrongLanguage = showTimeLanguage !== locale && showTimeLanguage != null
      const wrongSubtitle = subtitleLanguage !== locale && subtitleLanguage != null

      return wrongLanguage || wrongSubtitle
    },
    [locale],
  )

  const getLanguageWarningMessage = useCallback(
    (showTimeLanguage: string, subtitleLanguage: string) => {
      const wrongLanguage = showTimeLanguage !== locale && showTimeLanguage != null
      const wrongSubtitle = subtitleLanguage !== locale && subtitleLanguage != null

      if (wrongLanguage && wrongSubtitle) {
        return t(
          'showtimeLanguageAndSubtitlesWarning',
          'The showtime you have selected is in {{language}} with {{subtitleLanguage}} subtitles',
          { language: getLanguageName(showTimeLanguage), subtitleLanguage: getLanguageName(subtitleLanguage) },
        )
      }

      if (wrongLanguage) {
        return t('showtimeLanguageWarning', 'The showtime you have selected is in {{language}}.', {
          language: getLanguageName(showTimeLanguage),
        })
      }

      if (wrongSubtitle) {
        return t('showtimeSubtitleWarning', 'The showtime you have selected has {{subtitleLanguage}} subtitles.', {
          subtitleLanguage: getLanguageName(subtitleLanguage),
        })
      }
    },
    [locale, t],
  )

  const handleLanguageWarning = useCallback(
    (
      setLocaleWarningModalState: React.Dispatch<React.SetStateAction<LocaleWarningModalState>>,
      showTime: TheatricalShowtime,
      callback: () => void,
    ) => {
      const languageWarningMessage = getLanguageWarningMessage(
        showTime?.language as string,
        showTime?.subtitleLanguage as string,
      )
      if (languageWarningMessage) {
        return setLocaleWarningModalState({
          isOpen: true,
          callback: () => {
            callback()
          },
          warning: languageWarningMessage,
        })
      }
    },
    [getLanguageWarningMessage],
  )

  const trackTicketIntent = useCallback(
    (analyticsPayload: TicketsAnalyticsPayload) => {
      publishTicketIntent(analyticsPayload)
      track('Tickets Checkout Intent', analyticsPayload)
    },
    [track],
  )

  useEffect(() => {
    const shouldShowTicketUpdateShowtimeError = getBooleanFromLocalStorage('TICKET_SHOWTIME_SET_ERROR')
    if (shouldShowTicketUpdateShowtimeError) {
      showToast(t('couldntSelectShowtimeSelectAnother', 'We are unable to select that showtime, please try another!'))
      writeToLocalStorage('TICKET_SHOWTIME_SET_ERROR', false)
    }
  }, [showToast, t])

  const value = useMemo(() => {
    return {
      query,
      isDatesDisabled,
      isFreeTicketPromo,
      isKidsGoFree,
      routerIsReady,
      releaseDate,
      earlyDateToTry,
      handleMobileShare,
      openSharingModal,
      closeSharingModal,
      disableShowtimes,
      setDisableShowtimes,
      theatricalRelease,
      guildDiscountCodes,
      guildTicketsCount,
      detectLanguageDifference,
      trackTicketIntent,
      shouldHideDiscountsForMarketing,
      handleLanguageWarning,
      isEditReservationFlow,
      countryCode: theatricalRelease.region?.countryCode,
      experiments,
      basicPageExperimentEnabled,
      projectGuildScore,
    }
  }, [
    query,
    isDatesDisabled,
    isFreeTicketPromo,
    isKidsGoFree,
    routerIsReady,
    releaseDate,
    earlyDateToTry,
    handleMobileShare,
    openSharingModal,
    closeSharingModal,
    disableShowtimes,
    theatricalRelease,
    guildDiscountCodes,
    guildTicketsCount,
    detectLanguageDifference,
    trackTicketIntent,
    shouldHideDiscountsForMarketing,
    handleLanguageWarning,
    isEditReservationFlow,
    experiments,
    basicPageExperimentEnabled,
    projectGuildScore,
  ])

  return (
    <TicketsContext.Provider value={value}>
      {children}
      <SharingModal
        className="z-[80]"
        open={displaySharingModal}
        onClose={closeSharingModal}
        url={`${paths.getBaseURL()}${currentPathWithoutQueryParams}`}
      />
    </TicketsContext.Provider>
  )
}

export const useTicketsContext = (): TicketsContextValues => {
  return React.useContext(TicketsContext) as TicketsContextValues
}
